<script setup lang="ts">import { createPropsRestProxy } from '/vue-macros/reactivity-transform/helper';
const props = createPropsRestProxy(__props, ["brandFormDataStorageKey","hideBrand","hideCreateCase"]);

import qs from "qs";
import debounce from "lodash.debounce";
import { computed, nextTick, onMounted, ref, watch } from "vue";

import { caseService } from "@/services";
import { useBrandStore, useMessageStore } from "@/stores";
import { dateInputToUTCDateTime } from "@/services/UtilitiesService";

import { RETENTION_EXPIRY_DATE_FIELD_PATH, RETENTION_STATUS_FIELD_PATH } from "@/stores/brand-store";

import BrandSelect from "@/components/BrandSelect.vue";
import BrandLensSelect from "@/components/BrandLensSelect.vue";

import CaseSearchInput from "@/components/CaseSearch/CaseSearchInput.vue";
import CaseSearchCreate from "@/components/CaseSearch/CaseSearchCreate.vue";
import CaseSearchSelect from "@/components/CaseSearch/CaseSearchSelect.vue";
import CaseSearchNumber from "@/components/CaseSearch/CaseSearchNumber.vue";
import CaseSearchBoolean from "@/components/CaseSearch/CaseSearchBoolean.vue";
import CaseSearchFreeText from "@/components/CaseSearch/CaseSearchFreeText.vue";
import CaseSearchDateRange from "@/components/CaseSearch/CaseSearchDateRange.vue";
import CaseSearchAutocomplete from "@/components/CaseSearch/CaseSearchAutocomplete.vue";

import { ODATA_FILTER_GROUP_NAME, RETENTION_FILTER_GROUP_NAME } from "@/models/CaseSearchResult";

import type { Column } from "@/models/Column";
import type { PagedData } from "@/models/PagedData";
import type { ChangePagination, SortOrder } from "@/pages/CaseSearch.vue";
import type { CaseSearchPersistedFilter, CaseSearchRawBody, CaseSearchResult } from "@/models/CaseSearchResult";

withDefaults(defineProps<{
  page: number;
  pageSize: number;
  hideBrand?: boolean;
  hideCreateCase?: boolean;
  columnSortDirection: SortOrder;
  brandFormDataStorageKey: string;
}>(), { hideBrand: false,hideCreateCase: false, })
const emit = defineEmits({
  searchCallback: null as unknown as (data: PagedData<CaseSearchResult[]>, pageSize: number) => void,
  changePagination: null as unknown as ChangePagination,
  resetKeyChange: null as unknown as (key: string) => void,
});
const isLoading = defineModel<boolean>("isLoading", { required: true });
const currentError = defineModel<string | undefined>("error", { required: true });

const form = ref<HTMLFormElement | null>(null);
const resetKey = ref(Math.random().toString(36));
const lastSearch = ref(
  (() => {
    try {
      const persistedFilterStored = sessionStorage.getItem(__props.brandFormDataStorageKey);
      if (persistedFilterStored == null) throw new Error("Persisted Filter Not Found");

      return JSON.parse(persistedFilterStored) as CaseSearchPersistedFilter;
    } catch (error) {}
  })(),
);

defineExpose({ lastSearch, forceLastSearch, refreshPage: () => doSearch(true) });

const selectedLens = computed(() => useBrandStore().selectedLens);
const selectedBrand = computed(() => useBrandStore().selectedBrand);
const selectedClient = computed(() => useBrandStore().selectedClient);
const caseSearch = computed(() => useBrandStore().caseSearchConfig);

const groupedColumns = computed(() => {
  const caseSearch = useBrandStore().caseSearchConfig;

  const filterable: Column[] = [];
  const searchable: Column[] = [];
  const map = new Map<string, Column>();

  for (const col of caseSearch) {
    if (!col.filterable && !col.searchable) continue;

    map.set(col.fieldPath, col);
    if (col.searchable) searchable.push(col);
    if (col.filterable && col.filterConfig != null) filterable.push(col);
  }

  return { filterable, searchable, map };
});

function fieldOdataExpression(
  path: string,
  rawIsCollection: "true" | "false",
  options:
    | { searchFunction: "search.in" | "search.ismatch"; values: string[] }
    | {
        searchFunction?: undefined;
        conditions: Array<[string, string | boolean | number | null | undefined]>;
        join?: "and" | "or";
      },
) {
  const isCollection = rawIsCollection === "true";
  // Match the first two leafs, and all the subsequent ones delimited with the "/"
  const [, main, sub = ""] = /^(.+?\/[^/]+)(.+)?/.exec(path) ?? ["", path, ""];

  if (options.searchFunction === "search.in") {
    if (isCollection) return `${main}/any(it: search.in(it${sub}, '${options.values.join(",")}', ','))`;
    else return `search.in(${path}, '${options.values.join(",")}', ',')`;
  }

  if (options.searchFunction === "search.ismatch") {
    return `search.ismatch('${options.values.join(" ").replace("'", "''")}', '${path}', 'simple', 'all')`;
  }

  if (options.searchFunction != null) return;

  const valid = options.conditions.filter(([, value]) =>
    typeof value === "string" ? value.length > 0 : value != null,
  );
  if (valid.length < 1) return "";

  if (isCollection)
    return `${main}/any(it: ${valid
      .map(([operator, value]) => `(it${sub} ${operator} ${value})`)
      .join(` ${options.join ?? "and"} `)})`;
  else return valid.map(([operator, value]) => `(${path} ${operator} ${value})`).join(` ${options.join ?? "and"} `);
}

function getFilters(formData: FormData): CaseSearchPersistedFilter {
  const rawFilters = qs.parse(
    qs.stringify(
      [...formData.entries()].reduce(
        (acc, [key, value]) => {
          if (!(key in acc)) acc[key] = value;
          else if (Array.isArray(acc[key])) acc[key].push(value);
          else acc[key] = [acc[key], value];

          return acc;
        },
        {} as Record<string, FormDataEntryValue | FormDataEntryValue[]>,
      ),
      {
        encoder: (str, defaultEncoder, charset, type) =>
          defaultEncoder(type === "value" && typeof str === "string" ? str.trim() : str, defaultEncoder, charset),
        filter: (_, value) => {
          const trimmed = typeof value === "string" ? value.trim() : value;
          return trimmed === "" || trimmed == null ? undefined : trimmed;
        },
      },
    ),
    { depth: 50, strictDepth: true, arrayLimit: 100, throwOnLimitExceeded: true },
  ) as unknown as CaseSearchRawBody;
  const {
    [ODATA_FILTER_GROUP_NAME]: odata = {},
    [RETENTION_FILTER_GROUP_NAME]: retention = {},
    ...freeSearch
  } = rawFilters;

  return {
    rawFilters,
    /* Free Text */
    searchText: freeSearch.freeSearchText,
    searchMode: freeSearch.freeSearchText == null ? undefined : freeSearch.freeSearchMode,
    searchFields:
      freeSearch.freeSearchText == null || freeSearch.freeSearchFields == null
        ? undefined
        : (JSON.parse(freeSearch.freeSearchFields) as string[]),
    /* Field specific */
    filter: Object.entries(odata)
      .reduce((acc, [key, value]) => {
        if (value.value == null) return acc;

        let result: string | undefined;
        switch (value.type) {
          case "boolean":
            result = fieldOdataExpression(key, value.isCollection, { conditions: [["eq", value.value === "true"]] });
            break;
          case "number":
            result = fieldOdataExpression(key, value.isCollection, { conditions: [["eq", value.value]] });
            break;
          case "select":
            result = fieldOdataExpression(key, value.isCollection, {
              conditions: [value.value]
                .flat()
                .map((val) => ["eq", value.parseAs !== "literal" ? `'${val.replace("'", "''")}'` : val]),
              join: "or",
            });
            break;
          case "date-range": {
            const { from, to } = value.value;

            result = fieldOdataExpression(key, value.isCollection, {
              conditions: [
                ["ge", from ? dateInputToUTCDateTime(from).toISOString() : null],
                ["le", to ? dateInputToUTCDateTime(to, true).toISOString() : null],
              ],
            });
            break;
          }
          case "time-range": {
            const { from, to } = value.value;

            const [year, month, day] = new Date().toISOString().split(/-|T/);
            const startOfToday = new Date(+year, +month - 1, +day).getTime();

            // Convert time string to milliseconds comparing to start of today
            result = fieldOdataExpression(key, value.isCollection, {
              conditions: [
                ["ge", from ? new Date(`${year}-${month}-${day} ${from}`).getTime() - startOfToday : null],
                ["le", to ? new Date(`${year}-${month}-${day} ${to}`).getTime() - startOfToday : null],
              ],
            });
            break;
          }
          case "autocomplete": {
            result = fieldOdataExpression(key, value.isCollection, {
              searchFunction: "search.in",
              values: value.value,
            });
            break;
          }
          case "text":
            result = fieldOdataExpression(key, value.isCollection, {
              searchFunction: "search.ismatch",
              values: [value.value],
            });
            break;
        }

        if (result == null || result.length < 1) return acc;

        acc.push(result);

        return acc;
      }, [] as string[])
      .join(" and "),
    /* Retention */
    ...(() => {
      const expiryDate = (() => {
        const filter = retention[RETENTION_EXPIRY_DATE_FIELD_PATH];
        if (filter?.type !== "date-range" || filter.value == null) return null;

        return dateInputToUTCDateTime(filter.value.from).toISOString();
      })();

      const retentionFilter = (() => {
        const filter = retention[RETENTION_STATUS_FIELD_PATH];
        if (filter?.type !== "select") return "All";

        return (filter.value ?? "All") as CaseSearchPersistedFilter["retentionFilter"];
      })();

      if (retentionFilter === "ExpiresBefore") {
        if (expiryDate == null) {
          try {
            const el = document.querySelector<HTMLInputElement>(
              `[name*="${RETENTION_EXPIRY_DATE_FIELD_PATH}"]:not([type=hidden])`,
            );
            if (el == null) throw new Error("Expiry Date field not found");

            el.focus();
            el.scrollIntoView({ behavior: "smooth" });
          } catch (e) {
            if (import.meta.env.DEV) throw e;
          }

          throw new Error(
            `The "${groupedColumns.value.map.get(RETENTION_EXPIRY_DATE_FIELD_PATH)?.displayName ?? "Expiry Date"}" field is required.`,
          );
        }
        return { retentionFilter, expiryDate };
      }

      return { retentionFilter, expiryDate: null };
    })(),
    /* Other */
    select: ["id", "caseReference", ...caseSearch.value.map((col) => col.fieldPath)],
    lensCode: selectedLens.value.code,
    orderBy: getSearchOrder(),
  };
}

function getSearchOrder() {
  const orderByMap = {
    caseReference: "asc",
    "search.score()": "desc",
    [props.columnSortDirection.path]: props.columnSortDirection.order,
  };

  return [
    ...new Set([
      `${props.columnSortDirection.path} ${orderByMap[props.columnSortDirection.path]}`,
      `search.score() ${orderByMap["search.score()"]}`,
      `caseReference ${orderByMap.caseReference}`,
    ]),
  ];
}

const doSearch = debounce(async (usePersistedFilter: boolean) => {
  if (selectedClient.value == null || selectedBrand.value == null) return;
  if (form.value == null) return;

  try {
    isLoading.value = true;
    currentError.value = undefined;

    const activeFilter =
      usePersistedFilter && lastSearch.value != null ? lastSearch.value : getFilters(new FormData(form.value));

    const { rawFilters: _, ...body } = activeFilter;
    const result = await caseService.SearchCases(
      selectedClient.value,
      selectedBrand.value,
      props.page,
      props.pageSize,
      body,
    );

    lastSearch.value = activeFilter;
    sessionStorage.setItem(__props.brandFormDataStorageKey, JSON.stringify(lastSearch.value));

    const maxPage = Math.max(1, Math.ceil(result.totalCount / props.pageSize));

    // If the page requested doesn't have enough data to return, re-update the page with the max possible
    if (maxPage < result.page) emit("changePagination", { page: maxPage });
    // save the data
    else emit("searchCallback", result, props.pageSize);
  } catch (error) {
    console.error(error);

    const message = error instanceof Error ? error.message : "Error completing search";
    currentError.value = message;

    useMessageStore().updateMessage({
      type: "error",
      autoHide: true,
      showReload: false,
      title: "Case Search",
      message,
    });
  } finally {
    isLoading.value = false;
  }
}, 200);

function onSubmit() {
  if (form.value == null) return;

  if (props.page === 1) doSearch(false /* Update search with new form data */);
  else emit("changePagination", { page: 1 }); // Reset page and let watch hook trigger search
}

function forceLastSearch(value: CaseSearchPersistedFilter | undefined) {
  lastSearch.value = value;
  resetKey.value = Math.random().toString(36);

  nextTick(onSubmit);
}

function onReset() {
  forceLastSearch(undefined);
}

const componentMapping = {
  "": "",
  "time-range": "",
  text: CaseSearchInput,
  select: CaseSearchSelect,
  number: CaseSearchNumber,
  boolean: CaseSearchBoolean,
  "date-range": CaseSearchDateRange,
  autocomplete: CaseSearchAutocomplete,
};

// Page, size or sort order change, trigger search with new values and first page
watch([() => props.columnSortDirection, () => props.page, () => props.pageSize], () => doSearch(true));

// Trigger search with new form data
onMounted(() => doSearch(false));
</script>
<template>
  <div class="tw-relative tw-flex tw-flex-col tw-gap-2 tw-py-1">
    <h1 class="tw-sr-only">Case Search</h1>
    <template v-if="!hideBrand">
      <BrandSelect :key="selectedBrand" />
      <BrandLensSelect />
    </template>
    <form
      ref="form"
      aria-label="Case Search Form"
      class="tw-flex tw-flex-1 tw-flex-col tw-justify-start tw-overflow-y-auto"
      @submit.prevent="onSubmit"
    >
      <div
        :key="resetKey"
        class="CaseSearchForm w-full tw-space-y-2 tw-overflow-y-auto tw-overflow-x-hidden tw-px-3 tw-py-2"
      >
        <CaseSearchFreeText :columns="groupedColumns.searchable" :last-search="lastSearch" />
        <template v-for="item in groupedColumns.filterable" :key="item.fieldPath">
          <component
            :is="componentMapping[item.filterConfig?.type ?? '']"
            :config="item.filterConfig"
            :field-path="item.fieldPath"
            :display-name="item.displayName"
            :is-collection="item.isCollection ?? true"
            :last-search="
              lastSearch?.rawFilters?.[item.filterConfig?.filterGroup ?? ODATA_FILTER_GROUP_NAME]?.[item.fieldPath]
            "
          />
          <div
            v-if="item.filterConfig?.isGroupEnd === true"
            aria-hidden="true"
            class="!-tw-mb-1 !tw-mt-3 tw-h-px tw-w-full tw-bg-gray-500"
          />
        </template>
      </div>
      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2">
        <CaseSearchCreate v-if="!hideCreateCase" />
        <div v-if="currentError != null || isLoading" class="tw-text-sm empty:tw-hidden">
          <div v-if="currentError != null" class="tw-text-sm">Error!</div>
          <div v-else-if="isLoading" class="tw-flex tw-items-center tw-gap-1 tw-text-sm">
            <i class="fas fa-circle-notch fa-spin fa-sm" />
            Loading...
          </div>
        </div>
        <div class="tw-flex-1"></div>
        <button type="button" class="tw-btn-md-gray tw-min-w-20" @click="onReset">Reset</button>
        <button type="submit" class="tw-btn-md-green tw-min-w-20">Search</button>
      </div>
    </form>
  </div>
</template>
<style lang="scss">
.CaseSearchForm,
.AuditSearchForm {
  select:not([multiple]),
  select[multiple="false"] {
    appearance: none;
    background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgLTk2MCA5NjAgOTYwIiB3aWR0aD0iNDgiPjxwYXRoIGQ9Ik00ODAtMzgwIDI3Ni01ODRsMTYtMTYgMTg4IDE4OCAxODgtMTg4IDE2IDE2LTIwNCAyMDRaIi8+PC9zdmc+");
    background-size: 24px;
    background-repeat: no-repeat;
    background-position: calc(100% - 4px) center;
  }
}
</style>
